/**
 * The Network board's visual grammar, in one place so the KPI row, the badges,
 * the meters and the event feed can never disagree about what "degraded" means.
 *
 * Threshold and state logic lives in the backend (`nova.network.service`); this
 * module only maps the state it is given onto design-system tones and tokens,
 * and formats the numbers Cato returns into something a human reads.
 */

import type { NetworkSite, NetworkStatusCounts } from "@/lib/api";

/** The five connectivity states the API can report for a site. */
export type SiteState = NetworkSite["connectivity"];

/** Tone shared by `Badge`, `Statistic`, `Meter` and `MiniSparkline`. */
export type StatusTone = "success" | "warning" | "danger" | "neutral";

export const STATE_LABEL: Record<SiteState, string> = {
  connected: "Connected",
  degraded: "Degraded",
  disconnected: "Disconnected",
  disabled: "Disabled",
  unknown: "Unknown",
};

/**
 * A disabled site is down on purpose, so it is neutral rather than dangerous —
 * colouring it red would train people to ignore red.
 */
export const STATE_TONE: Record<SiteState, StatusTone> = {
  connected: "success",
  degraded: "warning",
  disconnected: "danger",
  disabled: "neutral",
  unknown: "neutral",
};

/** Segment colours for the estate composition bar. */
export const STATE_COLOR: Record<SiteState, string> = {
  connected: "var(--norican-success)",
  degraded: "var(--norican-warning)",
  disconnected: "var(--norican-danger)",
  disabled: "var(--norican-bg-muted)",
  unknown: "var(--norican-border)",
};

/** Order used wherever states are listed, worst first after the healthy one. */
export const STATE_ORDER: readonly SiteState[] = [
  "connected",
  "degraded",
  "disconnected",
  "disabled",
  "unknown",
];

/** Loss above this is treated as a problem worth colouring. */
const LOSS_WARNING_PCT = 1;
const LOSS_DANGER_PCT = 3;
/** Round-trip time thresholds, in milliseconds. */
const RTT_WARNING_MS = 80;
const RTT_DANGER_MS = 150;
/** Jitter thresholds, in milliseconds. */
const JITTER_WARNING_MS = 15;
const JITTER_DANGER_MS = 30;
/**
 * Host-licence utilisation thresholds, as a percentage of the site's limit.
 *
 * A site on its limit stops handing addresses to new devices, which arrives as
 * "the wifi is broken" — so the warning has to land before the site is full.
 */
const HOSTS_WARNING_PCT = 85;
const HOSTS_DANGER_PCT = 98;

/**
 * Read the API's count block into a fully-populated record.
 *
 * The generated type makes each counter optional (they carry server-side
 * defaults), so this collapses the `?? 0` noise into one place.
 */
export function stateCounts(counts: NetworkStatusCounts): Record<SiteState, number> {
  return {
    connected: counts.connected ?? 0,
    degraded: counts.degraded ?? 0,
    disconnected: counts.disconnected ?? 0,
    disabled: counts.disabled ?? 0,
    unknown: counts.unknown ?? 0,
  };
}

/** Tone for a packet-loss percentage. */
export function lossTone(pct: number | null | undefined): StatusTone {
  if (pct == null) {
    return "neutral";
  }
  if (pct >= LOSS_DANGER_PCT) {
    return "danger";
  }
  return pct >= LOSS_WARNING_PCT ? "warning" : "success";
}

/** Tone for a round-trip time in milliseconds. */
export function rttTone(ms: number | null | undefined): StatusTone {
  if (ms == null) {
    return "neutral";
  }
  if (ms >= RTT_DANGER_MS) {
    return "danger";
  }
  return ms >= RTT_WARNING_MS ? "warning" : "success";
}

/** Tone for a jitter reading in milliseconds. */
export function jitterTone(ms: number | null | undefined): StatusTone {
  if (ms == null) {
    return "neutral";
  }
  if (ms >= JITTER_DANGER_MS) {
    return "danger";
  }
  return ms >= JITTER_WARNING_MS ? "warning" : "success";
}

/**
 * Tone for a site's host count against its licensed limit.
 *
 * Neutral whenever either half is unread — an unknown limit is not headroom.
 */
export function hostsTone(
  count: number | null | undefined,
  limit: number | null | undefined,
): StatusTone {
  if (count == null || limit == null || limit <= 0) {
    return "neutral";
  }
  const pct = (count / limit) * 100;
  if (pct >= HOSTS_DANGER_PCT) {
    return "danger";
  }
  return pct >= HOSTS_WARNING_PCT ? "warning" : "success";
}

/**
 * Format a host count against its limit as `412 / 500`.
 *
 * Each side falls back to an em dash on its own, so a known count with an
 * unknown limit reads `412 / —` rather than implying unlimited headroom.
 */
export function formatHosts(
  count: number | null | undefined,
  limit: number | null | undefined,
): string {
  if (count == null && limit == null) {
    return "—";
  }
  return `${count ?? "—"} / ${limit ?? "—"}`;
}

/** Format a byte count into the largest sensible unit. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) {
    return "—";
  }
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let value = Math.abs(bytes);
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 100 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

/**
 * Average throughput in Mbps for a byte total observed over `windowSeconds`.
 *
 * Cato reports the window's byte total, not a rate, so the board has to divide
 * it back out before it can be compared with a provisioned line speed.
 */
export function averageMbps(
  bytes: number | null | undefined,
  windowSeconds: number,
): number | null {
  if (bytes == null || windowSeconds <= 0) {
    return null;
  }
  return (bytes * 8) / windowSeconds / 1_000_000;
}

/** Format a Mbps figure, dropping to two decimals for small values. */
export function formatMbps(mbps: number | null | undefined): string {
  if (mbps == null) {
    return "—";
  }
  if (mbps >= 1000) {
    return `${(mbps / 1000).toFixed(2)} Gbps`;
  }
  return `${mbps >= 10 ? Math.round(mbps) : mbps.toFixed(2)} Mbps`;
}

/** Format a duration in seconds as a coarse "18d 7h" / "4h 12m" / "9m". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0) {
    return "—";
  }
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

/** Format a millisecond reading for a latency/jitter cell. */
export function formatMs(ms: number | null | undefined): string {
  return ms == null ? "—" : `${ms >= 100 ? Math.round(ms) : ms.toFixed(1)} ms`;
}

/** Format a percentage for a loss cell. */
export function formatPct(pct: number | null | undefined, digits = 2): string {
  return pct == null ? "—" : `${pct.toFixed(digits)}%`;
}

/** Format an ISO timestamp as a short local date and time. */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }
  return parsed.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
